import type { AnalysisPlatform } from '@/shared/api/types/generated';

// ============================================================================
// Accès à la recherche Vestiaire Collective selon le plan (2026-10-01) —
// logique PURE derrière `useVestiaireAccess`. Contrat :
// docs/superpowers/api-changelog/2026-10-01-vestiaire-acces-par-plan.md.
// Le serveur refuse de toute façon la création (`createVestiaireAnalysis`) :
// le front ne verrouille que sur une réponse CERTAINE (`false`), jamais
// pendant le chargement ni sur un échec de la requête.
// ============================================================================

export interface VestiaireAccess {
  /** Accès accordé par le plan ; `true` tant que la réponse n'est pas connue. */
  hasAccess: boolean;
  /** Réponse du serveur pas encore arrivée. */
  loading: boolean;
}

/** État d'accès depuis la query : `value` = réponse (undefined tant qu'absente), `failed` = requête en échec. */
export const resolveVestiaireAccess = ({
  value,
  failed,
}: {
  value: boolean | undefined;
  failed: boolean;
}): VestiaireAccess => ({
  hasAccess: value ?? true,
  loading: value === undefined && !failed,
});

/** Verrouillé seulement une fois la réponse connue ET l'accès refusé. */
export const isVestiaireLocked = ({ hasAccess, loading }: VestiaireAccess): boolean => !loading && !hasAccess;

/** Plateforme de création effective : Vestiaire verrouillé (ex. état restauré) ⇒ retour à Vinted. */
export const creationPlatform = (platform: AnalysisPlatform, locked: boolean): AnalysisPlatform =>
  locked && platform === 'vestiaire' ? 'vinted' : platform;

/**
 * Plan courant observé par le sondage de l'abonnement : `undefined` = pas encore observé (réponse
 * absente), `null` = aucun plan. L'accès VC est à relire quand le plan CHANGE entre deux réponses
 * (retour de Stripe, résiliation, webhook arrivé pendant que la page sonde) — jamais à la
 * première observation, où la query d'accès vient elle-même d'être chargée.
 */
export const vestiaireAccessNeedsRefresh = (
  previousPlanId: string | null | undefined,
  nextPlanId: string | null | undefined,
): boolean => previousPlanId !== undefined && nextPlanId !== undefined && previousPlanId !== nextPlanId;
